/**
 * BackgroundJobsPage Component
 *
 * Main page for monitoring background jobs.
 * Allows filtering by application and status.
 */

import React, { useState, useMemo } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { DashboardLayout } from '../../../components/layout/DashboardLayout';
import { Button } from '../../../components/ui/Button';
import { Select } from '../../../components/ui/Select';
import { Alert } from '../../../components/ui/Alert';
import { BackgroundJobTable } from '../components/BackgroundJobTable';
import { BackgroundJobDetailsModal } from '../components/BackgroundJobDetailsModal';
import { useBackgroundJobs } from '../hooks/useBackgroundJobs';
import { useApplications } from '../../applications/hooks/useApplications';
import { useAuth } from '../../../contexts/AuthContext';
import { useLayoutProps } from '../../../hooks/useLayoutProps';
import type { BackgroundJob, JobStatus } from '../../../types/background-job.types';
import { getErrorMessage } from '../../../lib/utils/errors';

/**
 * Status filter options
 */
const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'running', label: 'Running' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
];

/**
 * BackgroundJobsPage component
 */
export const BackgroundJobsPage: React.FC = () => {
  const { tenant } = useAuth();
  const layoutProps = useLayoutProps();
  const [selectedApplicationId, setSelectedApplicationId] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<JobStatus | ''>('');
  const [selectedJob, setSelectedJob] = useState<BackgroundJob | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);

  const tenantId = tenant?.tenantId || '';

  // Fetch applications for the dropdown
  const {
    data: applications = [],
    isLoading: isLoadingApplications,
  } = useApplications(tenantId);

  // Build application options
  const applicationOptions = useMemo(() => {
    return [
      { value: '', label: 'Select an application...' },
      ...applications.map((app) => ({
        value: app.applicationId,
        label: `${app.applicationName} (${app.applicationCode})`,
      })),
    ];
  }, [applications]);

  // Fetch background jobs for the selected application
  const {
    data: jobsResponse,
    isLoading: isLoadingJobs,
    isFetching,
    error,
    refetch,
  } = useBackgroundJobs(tenantId, selectedApplicationId, {
    status: statusFilter || undefined,
    limit: 100,
    enabled: !!selectedApplicationId,
    // Auto-refresh every 5 seconds if there are running/pending jobs
    refetchInterval: (data) => {
      const hasActiveJobs = data?.data?.some(
        (job) => job.status === 'running' || job.status === 'pending'
      );
      return hasActiveJobs ? 5000 : false;
    },
  });

  const jobs = jobsResponse?.data || [];

  const handleViewDetails = (job: BackgroundJob) => {
    setSelectedJob(job);
    setShowDetailsModal(true);
  };

  const handleCloseDetails = () => {
    setShowDetailsModal(false);
    setSelectedJob(null);
  };

  return (
    <DashboardLayout {...layoutProps}>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Background Jobs</h1>
          <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            Monitor long-running background job executions
          </p>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4">
          <div className="w-80">
            <Select
              value={selectedApplicationId}
              onChange={(e) => setSelectedApplicationId(e.target.value)}
              options={applicationOptions}
              disabled={isLoadingApplications}
            />
          </div>
          <div className="w-48">
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as JobStatus | '')}
              options={STATUS_OPTIONS}
              disabled={!selectedApplicationId}
            />
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => refetch()}
            disabled={!selectedApplicationId || isFetching}
          >
            <ArrowPathIcon className={`h-4 w-4 mr-1 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* No Application Selected Message */}
        {!selectedApplicationId && (
          <div className="text-center py-12 rounded-lg" style={{ backgroundColor: 'var(--color-bg-sunken)' }}>
            <p style={{ color: 'var(--color-text-secondary)' }}>
              Select an application to view its background jobs
            </p>
          </div>
        )}

        {/* Error Alert */}
        {error && <Alert variant="error">{getErrorMessage(error)}</Alert>}

        {/* Jobs Table */}
        {selectedApplicationId && (
          <BackgroundJobTable
            jobs={jobs}
            isLoading={isLoadingJobs}
            onViewDetails={handleViewDetails}
          />
        )}

        {/* Details Modal */}
        {showDetailsModal && selectedJob && (
          <BackgroundJobDetailsModal
            isOpen={showDetailsModal}
            onClose={handleCloseDetails}
            tenantId={tenantId}
            jobId={selectedJob.jobId}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

export default BackgroundJobsPage;
